import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import DeprecatedUserMultiSelect from '~/components/Client/DeprecatedUserMultiSelect';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { parseDate } from '~/eds/src/utils/parseDate';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { withUsers } from '~/hocs/withUsers';
import { injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';

import { MODAL_DEPARTMENT_TREE } from '../../../types/modal.types';
import { debounce } from '../../../utils/helper.utils';
import EcDataField from '../../Shared/EcDataField/EcDataField';
import EcDatepicker from '../../Shared/EcDatepicker/EcDatepicker';
import EcModal from '../../Shared/EcModal/EcModal';
import EcMultipleSelectValue from '../../Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import EcSelect from '../../Shared/EcSelect/EcSelect';
import CheckmarkIcon from '../../Shared/Icons/CheckmarkIcon';
import LoadingSpinner from '../../Shared/Icons/LoadingSpinner';
import PlusIcon from '../../Shared/Icons/PlusIcon';
import ReloadIcon from '../../Shared/Icons/ReloadIcon';
import styles from './AlertsFilters.styles';
import { preprocessSearchPayload } from './AlertsFilters.utils';

const initialFiltersState = {
  entered_name: { value: '' },
  last_sent_start_date: null,
  last_sent_end_date: null,
  upcoming_send_start_date: null,
  upcoming_send_end_date: null,
  selected_users: [],
  selected_departments: [],
  created_by: [],
  active: null,
  formExpanded: true,
  primaryModal: null,
};

class AlertsFilters extends Component {
  constructor(props) {
    super(props);

    this.handleOnResetClick = this.handleOnResetClick.bind(this);
    this.handleAddDepartmentClick = this.handleAddDepartmentClick.bind(this);
    this.handleDepartmentChange = this.handleDepartmentChange.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.debounceNameSearch = debounce(this.handleSearch, 400);
    this.state = {
      ...initialFiltersState,
    };
  }

  handleAddDepartmentClick() {
    this.setState({ primaryModal: MODAL_DEPARTMENT_TREE });
  }

  handleDepartmentChange(selected_departments) {
    this.setState({ selected_departments }, this.handleSearch);
  }

  handleHideModal() {
    this.setState({ primaryModal: null });
  }

  handleOnResetClick() {
    this.setState({ ...initialFiltersState }, () =>
      this.props.handleOnSearch({}),
    );
  }

  handleSearch() {
    const {
      loading: _loading,
      loadingError: _loadingError,
      clientsOptions: _clientsOptions,
      primaryModal: _primaryModal,
      ...filters
    } = this.state;

    const searchPayload = preprocessSearchPayload(filters);

    this.props.handleOnSearch(searchPayload);
  }

  renderDepartmentTreeModal() {
    const { currentUser, intl } = this.props;
    const { selected_departments } = this.state;

    return (
      <EcModal
        modalType={MODAL_DEPARTMENT_TREE}
        width="540px"
        client={currentUser.client}
        title={intl.formatMessage({
          id: 'AlertsFilters.selectDepartmentTitle',
          defaultMessage: 'Select a Department',
          description: 'Modal title for department selection',
        })}
        departmentsSelected={selected_departments}
        handleDepartmentChange={this.handleDepartmentChange}
        confirmButtonIcon={<CheckmarkIcon size="20" />}
        confirmButtonText={intl.formatMessage({
          id: 'AlertsFilters.selectDepartmentButton',
          defaultMessage: 'Select Department',
          description: 'Button text to confirm department selection',
        })}
        hideModal={this.handleHideModal}
      />
    );
  }

  renderRecipientsDepartments() {
    const { classes, intl } = this.props;
    const { selected_departments } = this.state;

    if (!selected_departments.length) {
      return (
        <span className={classes.departmentsPlaceholder}>
          {intl.formatMessage({
            id: 'AlertsFilters.noDepartmentsSelected',
            defaultMessage: "You haven't selected any departments.",
            description: 'Placeholder text when no departments are selected',
          })}
        </span>
      );
    }

    return selected_departments.map((department, index) => (
      <EcMultipleSelectValue
        label={department.department_name}
        key={`${department.department_name}-${index}`}
        marginBottom="8px"
        closeHandler={() => {
          const newDepartments = selected_departments.filter(
            (recDepartment) =>
              recDepartment.department_id !== department.department_id,
          );
          this.setState(
            { selected_departments: newDepartments },
            this.handleSearch,
          );
        }}
      />
    ));
  }

  renderDatepickerField(
    startDate,
    endDate,
    startDateName,
    endDateName,
    fieldTitle,
  ) {
    const { classes, intl, formatDate, flags } = this.props;
    const hasLocalization = flags[FlagType.LanguageSelect];
    const formattedDate = (date) => {
      if (date) {
        return formatDate(date, 'iso');
      } else {
        return null;
      }
    };

    return (
      <div className={classes.fieldBody}>
        <div className={classes.fieldDatepicker}>
          {endDate ? (
            <>
              {hasLocalization ? (
                <DateInput
                  value={startDate ? parseDate(startDate) : null}
                  onChange={(date) => {
                    this.setState(
                      { [startDateName]: formattedDate(date) },
                      this.handleSearch,
                    );
                  }}
                  max={parseDate(endDate)}
                />
              ) : (
                <EcDatepicker
                  date={startDate ? parseDate(startDate) : null}
                  placeholder={intl.formatMessage({
                    id: 'AlertsFilters.datePlaceholder',
                    defaultMessage: 'Select...',
                    description: 'Placeholder text for date picker',
                  })}
                  onDateChange={(date) => {
                    this.setState(
                      { [startDateName]: formattedDate(date) },
                      this.handleSearch,
                    );
                  }}
                  maxDate={parseDate(endDate)}
                  isClearable={true}
                  title={`${fieldTitle} from date`}
                />
              )}
            </>
          ) : (
            <>
              {hasLocalization ? (
                <DateInput
                  value={startDate ? parseDate(startDate) : null}
                  onChange={(date) => {
                    this.setState(
                      { [startDateName]: formattedDate(date) },
                      this.handleSearch,
                    );
                  }}
                />
              ) : (
                <EcDatepicker
                  date={startDate ? parseDate(startDate) : null}
                  placeholder={intl.formatMessage({
                    id: 'AlertsFilters.datePlaceholder',
                    defaultMessage: 'Select...',
                    description: 'Placeholder text for date picker',
                  })}
                  onDateChange={(date) => {
                    this.setState(
                      { [startDateName]: formattedDate(date) },
                      this.handleSearch,
                    );
                  }}
                  isClearable={true}
                  title={`${fieldTitle} from date`}
                />
              )}
            </>
          )}
        </div>
        {hasLocalization ? (
          <Layout ml={2}>
            <Text>
              {intl.formatMessage({
                id: 'AlertsFilters.dateSeparator',
                defaultMessage: 'to',
                description: 'Separator text between start and end dates',
              })}
            </Text>
          </Layout>
        ) : (
          <span className={classes.datesSeparator}>
            {intl.formatMessage({
              id: 'AlertsFilters.dateSeparator',
              defaultMessage: 'to',
              description: 'Separator text between start and end dates',
            })}
          </span>
        )}

        <div className={classes.fieldDatepicker}>
          {startDate ? (
            <>
              {hasLocalization ? (
                <DateInput
                  value={endDate ? parseDate(endDate) : null}
                  placeholder={intl.formatMessage({
                    id: 'AlertsFilters.datePlaceholder',
                    defaultMessage: 'Select...',
                    description: 'Placeholder text for date picker',
                  })}
                  onChange={(date) => {
                    this.setState(
                      { [endDateName]: formattedDate(date) },
                      this.handleSearch,
                    );
                  }}
                  min={parseDate(startDate)}
                />
              ) : (
                <EcDatepicker
                  date={endDate ? parseDate(endDate) : null}
                  placeholder={intl.formatMessage({
                    id: 'AlertsFilters.datePlaceholder',
                    defaultMessage: 'Select...',
                    description: 'Placeholder text for date picker',
                  })}
                  onDateChange={(date) => {
                    this.setState(
                      { [endDateName]: formattedDate(date) },
                      this.handleSearch,
                    );
                  }}
                  minDate={parseDate(startDate)}
                  isClearable={true}
                  title={`${fieldTitle} to date`}
                />
              )}
            </>
          ) : (
            <>
              {hasLocalization ? (
                <DateInput
                  value={endDate ? parseDate(endDate) : null}
                  placeholder={intl.formatMessage({
                    id: 'AlertsFilters.datePlaceholder',
                    defaultMessage: 'Select...',
                    description: 'Placeholder text for date picker',
                  })}
                  onChange={(date) => {
                    this.setState(
                      { [endDateName]: formattedDate(date) },
                      this.handleSearch,
                    );
                  }}
                />
              ) : (
                <EcDatepicker
                  date={endDate ? parseDate(endDate) : null}
                  placeholder={intl.formatMessage({
                    id: 'AlertsFilters.datePlaceholder',
                    defaultMessage: 'Select...',
                    description: 'Placeholder text for date picker',
                  })}
                  onDateChange={(date) => {
                    this.setState(
                      { [endDateName]: formattedDate(date) },
                      this.handleSearch,
                    );
                  }}
                  isClearable={true}
                  title={`${fieldTitle} to date`}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  renderLoadingContainer() {
    const { classes, intl } = this.props;
    const { loadingError } = this.state;

    let containerContent = <LoadingSpinner size="medium" />;

    if (loadingError) {
      containerContent = (
        <div className={classes.filtersErrorContainer}>
          <div className={classes.filtersErrorMessage}>
            {intl.formatMessage({
              id: 'AlertsFilters.loadingError',
              defaultMessage:
                "There's been an error with loading filters data.",
              description: 'Error message when filters fail to load',
            })}
          </div>
          <button
            className={classes.filtersErrorAction}
            onClick={() => this.loadAlertFiltersData()}
          >
            {intl.formatMessage({
              id: 'AlertsFilters.retryButton',
              defaultMessage: 'Retry',
              description: 'Button text to retry loading filters',
            })}
          </button>
        </div>
      );
    }

    return <div className={classes.loadingContainer}>{containerContent}</div>;
  }

  calculateFiltersCount() {
    const {
      loading: _loading,
      loadingError: _loadingError,
      clientsOptions: _clientOptions,
      primaryModal: _primaryModal,
      ...filters
    } = this.state;
    return Object.values(filters).reduce((count, field) => {
      if (field && (field.length || field.value)) {
        return ++count;
      }
      return count;
    }, 0);
  }

  render() {
    const { classes, resultsCount, intl } = this.props;
    const {
      entered_name,
      last_sent_start_date,
      last_sent_end_date,
      upcoming_send_start_date,
      upcoming_send_end_date,
      selected_users,
      created_by,
      active,
      loading,
      loadingError,
      primaryModal,
    } = this.state;

    const filtersCount = this.calculateFiltersCount();
    const activeFieldValue = active && {
      label: active.display_value,
      value: active.value,
    };

    const filterFields = {
      nameField: {
        name: intl.formatMessage({
          id: 'AlertsFilters.filterByName',
          defaultMessage: 'Filter By Name',
          description: 'Label for name filter field',
        }),
        id: 'entered_name',
        value: '',
        type: 'STRING',
      },
      activeField: {
        id: 'active',
        value: {},
        values: [
          {
            display_value: intl.formatMessage({
              id: 'AlertsFilters.activeOption',
              defaultMessage: 'Active',
              description: 'Option for active alerts',
            }),
            value: 'True',
          },
          {
            display_value: intl.formatMessage({
              id: 'AlertsFilters.inactiveOption',
              defaultMessage: 'Inactive',
              description: 'Option for inactive alerts',
            }),
            value: 'False',
          },
        ],
        type: 'ARRAY_SINGLE',
        is_nullable: true,
      },
    };

    const activeFieldOptions = filterFields.activeField.values.map((val) => ({
      label: val.display_value,
      value: val.value,
    }));

    return (
      <div className={classes.alertsFiltersForm} role="search">
        <div className={classes.filterFormTitleWrapper}>
          <div className={classes.formTitle}>
            {intl.formatMessage({
              id: 'AlertsFilters.filterTitle',
              defaultMessage: 'Filter',
              description: 'Title for the filters section',
            })}{' '}
            {!!filtersCount && <span>{filtersCount}</span>}
          </div>
          {!!filtersCount && (
            <div className={classes.formTitleMeta}>
              <span className={classes.resultsMeta}>
                {resultsCount} {pluralize('result', resultsCount)}
              </span>
              <button
                aria-haspopup="true"
                className={classes.resetMeta}
                onClick={this.handleOnResetClick}
              >
                <ReloadIcon blue size="20" />
                {intl.formatMessage({
                  id: 'AlertsFilters.resetButton',
                  defaultMessage: 'Reset',
                  description: 'Button text to reset all filters',
                })}
              </button>
            </div>
          )}
        </div>
        <div className={classes.filterFormFields}>
          {(loading || loadingError) && this.renderLoadingContainer()}
          <div className={classes.formField}>
            <EcDataField
              field={filterFields.nameField}
              fieldValue={entered_name}
              fieldPlaceholder={intl.formatMessage({
                id: 'AlertsFilters.nameFilterPlaceholder',
                defaultMessage: 'Filter alerts by name',
                description: 'Placeholder text for name filter input',
              })}
              onFieldChange={(value) => {
                this.setState({ ...value });
                this.debounceNameSearch();
              }}
            />
          </div>
          <div className={classes.formField}>
            <div className={classes.fieldTitle}>
              {intl.formatMessage({
                id: 'AlertsFilters.lastSentLabel',
                defaultMessage: 'Last sent',
                description: 'Label for last sent date filter',
              })}
            </div>
            {this.renderDatepickerField(
              last_sent_start_date,
              last_sent_end_date,
              'last_sent_start_date',
              'last_sent_end_date',
              intl.formatMessage({
                id: 'AlertsFilters.lastSentLabel',
                defaultMessage: 'Last sent',
                description: 'Label for last sent date filter',
              }),
            )}
          </div>
          <div className={classes.formField}>
            <div className={classes.fieldTitle}>
              {intl.formatMessage({
                id: 'AlertsFilters.nextSendLabel',
                defaultMessage: 'Next send',
                description: 'Label for next send date filter',
              })}
            </div>
            {this.renderDatepickerField(
              upcoming_send_start_date,
              upcoming_send_end_date,
              'upcoming_send_start_date',
              'upcoming_send_end_date',
              intl.formatMessage({
                id: 'AlertsFilters.nextSendLabel',
                defaultMessage: 'Next send',
                description: 'Label for next send date filter',
              }),
            )}
          </div>
          {!loading && (
            <Fragment>
              <div className={classes.formField}>
                <div className={classes.fieldTitle}>
                  {intl.formatMessage({
                    id: 'AlertsFilters.recipientsUsersLabel',
                    defaultMessage: 'Recipients (Users)',
                    description: 'Label for user recipients filter',
                  })}
                </div>
                <DeprecatedUserMultiSelect
                  ariaLabel={intl.formatMessage({
                    id: 'AlertsFilters.recipientsUsersAriaLabel',
                    defaultMessage: 'recipients',
                    description: 'Aria label for user recipients filter',
                  })}
                  width="280px"
                  selectedUserIds={selected_users}
                  onChange={(selectedUserIds) =>
                    this.setState(
                      { selected_users: selectedUserIds },
                      this.handleSearch,
                    )
                  }
                />
              </div>
              <div className={classes.formField}>
                <div className={classes.fieldTitle}>
                  {intl.formatMessage({
                    id: 'AlertsFilters.recipientsDepartmentsLabel',
                    defaultMessage: 'Recipients (Departments)',
                    description: 'Label for department recipients filter',
                  })}
                </div>
                <div className={classes.fieldBody}>
                  {this.renderRecipientsDepartments()}
                </div>
                <div className={classes.fieldActions}>
                  <button
                    aria-haspopup="true"
                    className={classes.fieldAction}
                    onClick={this.handleAddDepartmentClick}
                  >
                    <PlusIcon isActive size="20" />{' '}
                    {intl.formatMessage({
                      id: 'AlertsFilters.addDepartmentButton',
                      defaultMessage: 'Add a Department',
                      description: 'Button text to add a department',
                    })}
                  </button>
                </div>
              </div>
              <div className={classes.formField}>
                <div className={classes.fieldTitle}>
                  {intl.formatMessage({
                    id: 'AlertsFilters.createdByLabel',
                    defaultMessage: 'Created By',
                    description: 'Label for created by filter',
                  })}
                </div>
                <DeprecatedUserMultiSelect
                  ariaLabel="created by"
                  width="280px"
                  selectedUserIds={created_by}
                  onChange={(selectedUserIds) =>
                    this.setState(
                      { created_by: selectedUserIds },
                      this.handleSearch,
                    )
                  }
                />
              </div>
            </Fragment>
          )}
          <div className={classes.formField}>
            <EcSelect
              aria-label={intl.formatMessage({
                id: 'AlertsFilters.activeInactiveAriaLabel',
                defaultMessage: 'alert status active or inactive',
                description: 'Aria label for the active/inactive status filter',
              })}
              value={activeFieldValue}
              options={activeFieldOptions}
              onChange={(newValue) => {
                const newState = newValue
                  ? { display_value: newValue.label, value: newValue.value }
                  : null;
                this.setState({ active: newState }, this.handleSearch);
              }}
              placeholder={intl.formatMessage({
                id: 'AlertsFilters.activeInactivePlaceholder',
                defaultMessage: 'Active and Inactive',
                description: 'Placeholder text for active/inactive filter',
              })}
              isClearable
            />
          </div>
        </div>
        {primaryModal === MODAL_DEPARTMENT_TREE &&
          this.renderDepartmentTreeModal()}
      </div>
    );
  }
}

AlertsFilters.propTypes = {
  collapsed: PropTypes.bool,
  handleOnSearch: PropTypes.func.isRequired,
  resultsCount: PropTypes.number,
};

const mapStateToProps = ({ currentUser }) => ({ currentUser });

AlertsFilters = withUsers(
  connect(mapStateToProps)(injectIntl(withFormatDate(AlertsFilters))),
);

export default injectSheet(styles)(withFlags(AlertsFilters));
